/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import { DrawingManager, Polygon } from '@react-google-maps/api';
import {
  openAreaResultsTray,
  setMapFilters,
  setDrawingDone,
  setDrawingPath,
  setDrawingProps
} from '@actions/map-actions';
import { mapConfig } from '@constants/component-configs';
import { getEntityCount } from '@utils/entity-utils';
import {
  convertFromGoogleMapsShape,
  getFiltersFromGoogleMapsShape,
  getLineDistance,
  getShapeArea,
  renderDistance,
  setShapeEventListener
} from '@utils/map-tools-utils';
import { convertToGoogleMapsShape } from '@utils/map-utils';
import { destroyMeasurement } from '@utils/measurement-utils';
import MeasurementTool from './measurement-tool';
import ShapeTool from './shape-tool';

const DrawingTools = () => {
  const dispatch = useDispatch();

  // Selectors
  const done = useSelector(state => state.map.drawing.done);
  const mode = useSelector(state => state.map.drawing.mode);
  const shape = useSelector(state => state.map.drawing.shape);
  const path = useSelector(state => state.map.drawing.path);
  const entityCounts = useSelector(state => getEntityCount(state.entities.types));

  // State
  const [active, setActive] = useState(false);
  const [mapsShape, setMapsShape] = useState(null);
  const [complete, setComplete] = useState(false);

  // Refs - use ref to access current mapsShape in debounced callback
  const mapsShapeRef = useRef(null);
  const prevModeRef = useRef(mode);

  // Keep ref in sync with state for use in debounced callback
  useEffect(() => {
    mapsShapeRef.current = mapsShape;
  }, [mapsShape]);

  const removeShape = useCallback(shapeToRemove => {
    if (shapeToRemove && shapeToRemove.setMap) {
      shapeToRemove.setMap(null);
    }
  }, []);

  // calculateInside needs to access current mapsShape via ref
  // because it's called from a debounced function
  const calculateInside = useCallback(() => {
    const currentMapsShape = mapsShapeRef.current;
    if (currentMapsShape) {
      const geom_in = getFiltersFromGoogleMapsShape(currentMapsShape);
      dispatch(setMapFilters({ geom_in, shape_selection: true }));
      setActive(false);
    }
  }, [dispatch]);

  // Create debounced function once - stable reference
  const debounceCalculateInside = useMemo(
    () => debounce(calculateInside, 500),
    [calculateInside]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debounceCalculateInside.cancel();
    };
  }, [debounceCalculateInside]);

  const initializePage = useCallback(() => {
    if (shape) {
      const googleShape = convertToGoogleMapsShape(shape);
      const shapeWithListener = setShapeEventListener(googleShape, debounceCalculateInside);
      setComplete(true);
      setMapsShape(shapeWithListener);
      dispatch(openAreaResultsTray());
    }
  }, [shape, debounceCalculateInside, dispatch]);

  const calculateShape = useCallback(newMapsShape => {
    // Remove existing shape
    removeShape(mapsShapeRef.current);
    // Store new shape with event listener
    const shapeWithListener = setShapeEventListener(newMapsShape, debounceCalculateInside);
    setMapsShape(shapeWithListener);
    // Update Redux and calculate
    const shapeData = convertFromGoogleMapsShape(newMapsShape);
    dispatch(setDrawingProps({ shape: shapeData }));
    // Calculate inside (uses ref, so we need to update ref first)
    mapsShapeRef.current = shapeWithListener;
    const geom_in = getFiltersFromGoogleMapsShape(shapeWithListener);
    dispatch(setMapFilters({ geom_in, shape_selection: true }));
    setActive(false);
    dispatch(openAreaResultsTray());
  }, [dispatch, removeShape, debounceCalculateInside]);

  const resetShape = useCallback(() => {
    removeShape(mapsShapeRef.current);
    setMapsShape(null);
    setActive(false);
    setComplete(false);
    dispatch(setDrawingProps({ shape: null, mode: '' }));
    dispatch(setMapFilters({ geom_in: null, shape_selection: false }));
  }, [dispatch, removeShape]);

  const closeMeasurement = useCallback(() => {
    destroyMeasurement(value => dispatch(setDrawingDone(value)));
    dispatch(setDrawingProps({ mode: '', path: null }));
  }, [dispatch]);

  const resetMeasurement = useCallback(() => {
    destroyMeasurement(value => dispatch(setDrawingDone(value)));
    dispatch(setDrawingProps({ mode: 'measurement', path: null }));
  }, [dispatch]);

  const onPolygonComplete = useCallback(newMapsShape => {
    if (newMapsShape.getPath().getArray().length < 3) {
      removeShape(newMapsShape);
      return;
    }
    calculateShape(newMapsShape);
  }, [calculateShape, removeShape]);

  const onCircleComplete = useCallback(newMapsShape => {
    calculateShape(newMapsShape);
  }, [calculateShape]);

  const getEntities = useCallback(() => {
    const currentMapsShape = mapsShapeRef.current;
    if (currentMapsShape) {
      return [
        { label: 'Area', value: `${getShapeArea(currentMapsShape)} square miles` },
        ...(entityCounts ? entityCounts : [])
      ];
    }
    return null;
  }, [entityCounts]);

  // Initial mount
  useEffect(() => {
    initializePage();
    if (!active && Boolean(mode.length)) {
      setActive(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for mode changes
  useEffect(() => {
    if (prevModeRef.current !== mode) {
      setActive(true);
    }
    prevModeRef.current = mode;
  }, [mode]);

  // Memoize paths to prevent unnecessary resets
  const polygonPaths = useMemo(() => {
    if (mapsShape && mapsShape.getPath) {
      return mapsShape.getPath();
    }
    return null;
  }, [mapsShape]);

  // Render measurement tool
  if (mode === 'measurement') {
    const distance = getLineDistance(path);
    return (
      <div>
        <MeasurementTool
          done={done}
          closeWindow={closeMeasurement}
          distance={{
            label: 'Distance',
            value: renderDistance(distance)
          }}
          onMeasureDone={value => dispatch(setDrawingDone(value))}
          resetWindow={resetMeasurement}
          setDrawingPath={pathData => dispatch(setDrawingPath(pathData))}
        />
      </div>
    );
  }

  return (
    <div>
      {Boolean(mode.length) && active &&
        <DrawingManager
          drawingMode={mode}
          options={mapConfig.drawingManager}
          onPolygonComplete={onPolygonComplete}
          onCircleComplete={onCircleComplete}
        />
      }
      {mapsShape && complete && polygonPaths &&
        <Polygon
          editable
          paths={polygonPaths}
        />
      }
      {Boolean(mode.length) &&
        <ShapeTool
          closeWindow={resetShape}
          entities={getEntities()}
        />
      }
    </div>
  );
};

export default memo(DrawingTools);
